import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Upload, CheckCircle2, XCircle, ArrowLeft, Loader2,
  Plus, Trash2, ArrowRight, Sparkles, FileText, RefreshCw, Download,
  Send, ClipboardList, Pencil, Check, RotateCcw, AlertCircle,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Stable client-side id for new rows.
const newRowId = () => `itm_${Math.random().toString(36).slice(2, 12)}`;

// ─────────────────────────────────────────────────────────────────────────
// Post-submission status trajectory shown above the wizard.
//
// Visible only once the claim leaves `draft` (i.e. has been submitted at
// least once). Highlights the current stage on a 5-node track:
//   Submitted → In Process → Approved → Payment Initiated → Paid
//
// "Sent back" is detected via `claim.sent_back_pending` (backend-derived
// from the last status_history entry) and rendered as an amber banner
// above the tracker with the admin's note, so the secretary knows exactly
// which step needs revision before re-submitting.
// ─────────────────────────────────────────────────────────────────────────
const TRAJECTORY_STEPS = [
  { key: 'submitted',         label: 'Submitted' },
  { key: 'in_process',        label: 'In Process' },
  { key: 'approved',          label: 'Approved' },
  { key: 'payment_initiated', label: 'Payment Initiated' },
  { key: 'paid',              label: 'Paid' },
];

// ─────────────────────────────────────────────────────────────────────────
// Activity log — surfaces `claim.status_history` chronologically so the
// secretary can see who did what and when (created, submitted, admin
// approved amount, status change, sent back, etc.).
// ─────────────────────────────────────────────────────────────────────────
const STATUS_HISTORY_LABEL = {
  draft: 'Draft',
  submitted: 'Submitted',
  in_process: 'In Process',
  approved: 'Approved',
  payment_initiated: 'Payment Initiated',
  paid: 'Paid',
};

const ActivityLog = ({ history }) => {
  if (!history || history.length === 0) return null;
  // Render in reverse-chronological order (newest first) for relevance.
  const items = [...history].slice().reverse();
  return (
    <Card data-testid="wizard-activity-log">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <ClipboardList className="h-4 w-4" /> Activity
        </CardTitle>
        <CardDescription>
          Every change to this claim, in order — creation, submission, reviewer actions, status changes.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ol className="space-y-3">
          {items.map((h, i) => {
            const isSentBack = !!h.sent_back;
            const isAmountSet = h.action === 'approved_amount_set';
            const dotColor = isSentBack
              ? 'bg-amber-500'
              : h.status === 'approved' || h.status === 'paid'
              ? 'bg-emerald-600'
              : h.status === 'draft'
              ? 'bg-slate-400'
              : 'bg-sky-600';
            const headlineLabel = isSentBack
              ? 'Sent back for revision'
              : isAmountSet
              ? 'Approved amount set'
              : STATUS_HISTORY_LABEL[h.status] || h.status;
            return (
              <li
                key={`${h.status}-${h.at || i}`}
                className="flex items-start gap-3 text-sm"
                data-testid={`wizard-activity-row-${items.length - 1 - i}`}
              >
                <div className={`h-2 w-2 rounded-full ${dotColor} mt-2 flex-shrink-0`} />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-foreground">
                    {headlineLabel}
                    {h.approved_amount != null && (
                      <span className="ml-2 text-emerald-700 font-semibold">
                        ₹{Number(h.approved_amount).toLocaleString('en-IN')}
                      </span>
                    )}
                  </div>
                  {h.note && (
                    <div className="text-xs text-muted-foreground mt-0.5 break-words">
                      {h.note}
                    </div>
                  )}
                  <div className="text-[11px] text-muted-foreground/80 mt-1">
                    {h.at ? new Date(h.at).toLocaleString() : ''}
                    {h.by && <> · <span className="font-mono">{h.by}</span></>}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      </CardContent>
    </Card>
  );
};

const ClaimStatusTrajectory = ({ claim }) => {
  if (claim.status === 'draft' && !claim.submitted_at) return null;

  const sentBackPending = !!claim.sent_back_pending;
  const isPaid = claim.status === 'paid';
  const rawIdx = Math.max(0, TRAJECTORY_STEPS.findIndex((s) => s.key === claim.status));
  const currentIdx = isPaid ? TRAJECTORY_STEPS.length : rawIdx;

  const sentBackEntry = sentBackPending
    ? (claim.status_history || []).slice().reverse().find((h) => h.sent_back)
    : null;
  const adminNote = sentBackEntry?.note
    ? sentBackEntry.note.replace(/^Sent back:\s*/i, '')
    : '';

  // MPL has reduced the grant amount. Surface this prominently so the
  // secretary doesn't have to scroll to the activity log to find out.
  // We show the banner the moment the admin commits an approved amount
  // that's below the claimed total — regardless of status — so changes
  // are visible even while the claim is still being processed.
  const claimedAmount = Number(claim.claim_amount) || 0;
  const approvedAmount = Number(claim.approved_amount) || 0;
  const inrFmt = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
  const showPartialBanner =
    approvedAmount > 0
    && claimedAmount > 0
    && approvedAmount < claimedAmount;

  return (
    <div className="rounded-xl border bg-white p-4 sm:p-5 shadow-sm" data-testid="wizard-status-trajectory">
      {sentBackPending && (
        <div
          className="mb-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm flex items-start gap-2"
          data-testid="wizard-sent-back-banner"
        >
          <RotateCcw className="h-4 w-4 mt-0.5 text-amber-700 flex-shrink-0" />
          <div className="flex-1">
            <div className="font-semibold text-amber-900">
              MPL reviewer has sent this claim back for revision
            </div>
            {adminNote && (
              <div className="text-amber-900/80 text-xs mt-0.5">
                <span className="font-medium">Reviewer note: </span>{adminNote}
              </div>
            )}
            <div className="text-amber-900/70 text-[11px] mt-1.5 flex items-center gap-1">
              <AlertCircle className="h-3 w-3" />
              You can now edit <strong>any step (1–5)</strong> via its Edit button below. Re-submit on Step 6 when done.
            </div>
          </div>
        </div>
      )}

      {/* Partial-approval banner — surfaces the haircut MPL applied/proposed.
          Copy is tuned based on whether the claim is past "approved" or
          still being reviewed. */}
      {showPartialBanner && (
        <div
          className="mb-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm flex items-start gap-2"
          data-testid="wizard-partial-approval-banner"
        >
          <AlertCircle className="h-4 w-4 mt-0.5 text-amber-700 flex-shrink-0" />
          <div className="flex-1">
            <div className="font-semibold text-amber-900">
              {['approved', 'payment_initiated', 'paid'].includes(claim.status)
                ? `MPL has approved ${inrFmt(approvedAmount)} of your ${inrFmt(claimedAmount)} claim`
                : `MPL reviewer has proposed approving ${inrFmt(approvedAmount)} of your ${inrFmt(claimedAmount)} claim`}
            </div>
            <div className="text-amber-900/80 text-xs mt-0.5">
              Reduction of <strong>{inrFmt(claimedAmount - approvedAmount)}</strong>{' '}
              {['approved', 'payment_initiated', 'paid'].includes(claim.status)
                ? 'applied by the MPL reviewer. The disbursement will be for the approved amount only.'
                : 'noted by the MPL reviewer. The disbursement, once approved, will be for this reduced amount.'}
            </div>
          </div>
        </div>
      )}

      <ol className="flex items-start justify-between gap-1 sm:gap-2 overflow-x-auto pt-1">
        {TRAJECTORY_STEPS.map((step, i) => {
          const state = i < currentIdx ? 'completed' : i === currentIdx ? 'current' : 'pending';
          const dotCls =
            state === 'completed'
              ? 'bg-emerald-600 border-emerald-600 text-white'
              : state === 'current'
              ? (sentBackPending
                  ? 'border-amber-500 text-amber-700 bg-white ring-4 ring-amber-100'
                  : 'border-emerald-600 text-emerald-700 bg-white ring-4 ring-emerald-100')
              : 'border-slate-300 text-slate-400 bg-white';
          const labelCls =
            state === 'completed'
              ? 'text-emerald-700'
              : state === 'current'
              ? (sentBackPending ? 'text-amber-800 font-semibold' : 'text-emerald-800 font-semibold')
              : 'text-slate-400';
          const lineCls = i <= currentIdx ? 'bg-emerald-500' : 'bg-slate-200';
          return (
            <li
              key={step.key}
              className="flex-1 flex flex-col items-center min-w-[70px] relative"
              data-testid={`wizard-trajectory-${step.key}`}
            >
              {i > 0 && (
                <span
                  className={`absolute top-3.5 h-0.5 ${lineCls}`}
                  style={{ left: 'calc(-50% + 18px)', right: 'calc(50% + 18px)' }}
                  aria-hidden
                />
              )}
              <span
                className={`relative z-10 h-7 w-7 rounded-full border-2 grid place-items-center text-xs font-bold ${dotCls}`}
              >
                {state === 'completed' ? <Check className="h-3.5 w-3.5" /> : i + 1}
              </span>
              <span className={`mt-1.5 text-[10px] sm:text-xs text-center leading-tight ${labelCls}`}>
                {step.label}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
};

// Inline Edit-step button shown on every passed-step card header.
// Hoisted to module scope so React doesn't churn nested components on
// every render of the parent.
const EditStepButton = ({ step, downstream, label = 'Edit', canEdit, resettingStep, onReset }) => {
  if (!canEdit) return null;
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => onReset(step, downstream)}
      disabled={resettingStep != null}
      className="ml-2 text-xs"
      data-testid={`wizard-edit-step${step}-btn`}
    >
      {resettingStep === step
        ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Resetting…</>
        : <><Pencil className="h-3 w-3 mr-1" /> {label}</>
      }
    </Button>
  );
};

export default function PortalClaimWizard() {
  const { claimId } = useParams();
  const [claim, setClaim] = useState(null);

  // Step 1 state
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Step 2 state — local mirror of the table the user edits live.
  const [rows, setRows] = useState([]);
  const [savingRows, setSavingRows] = useState(false);
  // Track AI in-flight per item_id so the badge can show a spinner.
  const [justifyInFlight, setJustifyInFlight] = useState({});
  const [completing, setCompleting] = useState(false);

  // Step 3 state — quotation upload spinners per row + Step 3 complete flag.
  const [quotationInFlight, setQuotationInFlight] = useState({});
  const [completingStep3, setCompletingStep3] = useState(false);

  // Step 4 state — single letterhead upload spinner + Step 4 complete flag.
  const [letterheadInFlight, setLetterheadInFlight] = useState(false);
  const [completingStep4, setCompletingStep4] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);

  // Step 5 state — resolution upload + complete spinners.
  const [resolutionInFlight, setResolutionInFlight] = useState(false);
  const [completingStep5, setCompletingStep5] = useState(false);

  // Step 6 state — final submission spinner.
  const [submittingClaim, setSubmittingClaim] = useState(false);

  // Iter45 — reset-to-step spinner (one global at a time).
  const [resettingStep, setResettingStep] = useState(null);

  // Iter45 — sequential AI: derived flag is true while ANY Step 3 row
  // is mid-AI-check. Used to disable other file pickers / Re-upload
  // buttons across rows so only ONE quotation is verified at a time.
  // (Defined later, after quotationInFlight is in scope.)

  // Debounce timer for auto-save.
  const saveTimer = useRef(null);

  const loadClaim = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/portal/claims/${claimId}`, { withCredentials: true });
      setClaim(res.data);
      // Re-seed local rows from server when there's no pending local edit.
      setRows((prev) => {
        const serverItems = res.data?.proposed_items || [];
        if (!prev.length) return serverItems.map((r) => ({ ...r }));
        // If lengths/ids match, prefer server (refreshes verdicts); otherwise keep local.
        const sameShape =
          prev.length === serverItems.length &&
          prev.every((r, i) => r.id === serverItems[i].id);
        return sameShape ? serverItems.map((r) => ({ ...r })) : prev;
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load claim');
    }
  }, [claimId]);

  useEffect(() => { loadClaim(); }, [loadClaim]);

  // Iter45 — defensive: if loadClaim runs while a quotationInFlight key
  // is stale-true (e.g. user navigated away mid-AI-check), clear it so
  // the sequential gate doesn't dead-lock the Step 3 inputs.
  useEffect(() => {
    if (claim) setQuotationInFlight({});
  }, [claim?.id]);

  // ─── Step 1 ──────────────────────────────────────────────
  const runStep1 = async () => {
    if (!file) {
      toast.error('Please choose a PDF first');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(
        `${API}/portal/claims/${claimId}/wizard/step1`,
        fd,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      if (res.data?.verdict === 'specific') {
        toast.success(`Pass — ${res.data.items_extracted} items extracted`);
      } else {
        toast.error('Document is too generic — please revise and re-upload');
      }
      setFile(null);
      setRows([]); // force re-seed from server on next loadClaim
      await loadClaim();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'AI screen failed');
    } finally {
      setUploading(false);
    }
  };

  // ─── Step 2 — table editing ─────────────────────────────
  const updateRow = (idx, patch) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
    scheduleAutoSave();
  };

  const addRow = () => {
    setRows((prev) => [
      ...prev,
      {
        id: newRowId(),
        item: '',
        details: '',
        justification: '',
        estimated_cost: 0,
        timeline_weeks: 0,
        justification_verdict: null,
      },
    ]);
  };

  const removeRow = (idx) => {
    setRows((prev) => prev.filter((_, i) => i !== idx));
    scheduleAutoSave();
  };

  const persistRows = useCallback(async (rowsToSave) => {
    if (!rowsToSave.length) return;
    setSavingRows(true);
    try {
      const payload = {
        items: rowsToSave.map((r) => ({
          id: r.id,
          item: r.item || '',
          details: r.details || '',
          justification: r.justification || '',
          estimated_cost: Number(r.estimated_cost) || 0,
          timeline_weeks: Number(r.timeline_weeks) || 0,
        })),
      };
      const res = await axios.put(
        `${API}/portal/claims/${claimId}/wizard/step2/items`,
        payload,
        { withCredentials: true },
      );
      // Sync back any AI-verdict resets the backend just applied.
      setRows(res.data.items.map((r) => ({ ...r })));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // Backend returns a structured 400 when the eligible-amount cap
      // is breached. Render the message so the secretary knows exactly
      // what to trim.
      if (detail && typeof detail === 'object' && detail.message) {
        toast.error(detail.message);
      } else {
        toast.error(detail || 'Failed to save table');
      }
    } finally {
      setSavingRows(false);
    }
  }, [claimId]);

  const scheduleAutoSave = () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      setRows((latest) => {
        persistRows(latest);
        return latest;
      });
    }, 1500);
  };

  const justifyRow = async (row) => {
    if (!row?.id) return;
    const text = (row.justification || '').trim();
    if (text.length < 10) {
      toast.error('Write at least one full sentence before AI check.');
      return;
    }
    // Make sure the latest text is persisted FIRST, so the backend judges
    // exactly what's in the textarea (not a stale value from 1.5s ago).
    setJustifyInFlight((m) => ({ ...m, [row.id]: true }));
    try {
      await persistRows(rows);
      const res = await axios.post(
        `${API}/portal/claims/${claimId}/wizard/step2/justify/${row.id}`,
        null,
        { withCredentials: true },
      );
      const verdictObj = {
        verdict: res.data.verdict,
        remark: res.data.remark,
        missing_signals: res.data.missing_signals || [],
        suggested_rewrite: res.data.suggested_rewrite || '',
        actionable_tip: res.data.actionable_tip || '',
        evaluated_at: new Date().toISOString(),
        justified_text: text,
      };
      setRows((prev) =>
        prev.map((r) => (r.id === row.id ? { ...r, justification_verdict: verdictObj } : r))
      );
      if (res.data.verdict === 'pass') {
        toast.success('Justification accepted');
      } else {
        toast.error('Justification needs more cricket-specific detail');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'AI check failed');
    } finally {
      setJustifyInFlight((m) => ({ ...m, [row.id]: false }));
    }
  };

  const completeStep2 = async () => {
    setCompleting(true);
    try {
      await persistRows(rows);
      const res = await axios.post(
        `${API}/portal/claims/${claimId}/wizard/step2/complete`,
        null,
        { withCredentials: true },
      );
      toast.success(`Step 2 complete — ${res.data.row_count} rows approved`);
      await loadClaim();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.issues) {
        toast.error(detail.message);
        detail.issues.forEach((iss) => toast.error(iss));
      } else {
        toast.error(detail || 'Could not complete Step 2');
      }
    } finally {
      setCompleting(false);
    }
  };

  // ─── Step 3 — quotation upload per row ─────────────────
  const uploadQuotation = async (row, file) => {
    if (!row?.id || !file) return;
    setQuotationInFlight((m) => ({ ...m, [row.id]: true }));
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(
        `${API}/portal/claims/${claimId}/wizard/step3/quotation/${row.id}`,
        fd,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } },
      );
      if (res.data?.verdict === 'pass') {
        toast.success(`Quotation accepted for "${row.item || 'item'}"`);
      } else {
        toast.error(`Quotation failed AI checks for "${row.item || 'item'}"`);
      }
      await loadClaim();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Quotation upload failed');
    } finally {
      setQuotationInFlight((m) => ({ ...m, [row.id]: false }));
    }
  };

  const removeQuotation = async (row) => {
    if (!row?.id) return;
    if (!window.confirm('Remove this quotation and re-upload a new one?')) return;
    setQuotationInFlight((m) => ({ ...m, [row.id]: true }));
    try {
      await axios.delete(
        `${API}/portal/claims/${claimId}/wizard/step3/quotation/${row.id}`,
        { withCredentials: true },
      );
      toast.success('Quotation removed');
      await loadClaim();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not remove quotation');
    } finally {
      setQuotationInFlight((m) => ({ ...m, [row.id]: false }));
    }
  };

  const completeStep3 = async () => {
    setCompletingStep3(true);
    try {
      const res = await axios.post(
        `${API}/portal/claims/${claimId}/wizard/step3/complete`,
        null,
        { withCredentials: true },
      );
      toast.success(`Step 3 complete — ${res.data.row_count} quotations verified`);
      await loadClaim();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.issues) {
        toast.error(detail.message);
        detail.issues.forEach((iss) => toast.error(iss));
      } else {
        toast.error(detail || 'Could not complete Step 3');
      }
    } finally {
      setCompletingStep3(false);
    }
  };

  // ─── Step 4 — consolidated letterhead upload ───────────
  const downloadLetterheadTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      const res = await axios.get(
        `${API}/portal/claims/${claimId}/wizard/step4/template-pdf`,
        { withCredentials: true, responseType: 'blob' },
      );
      // Pull filename from Content-Disposition if present.
      const cd = res.headers['content-disposition'] || '';
      const match = cd.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `letterhead-template-${claimId.slice(0, 8)}.pdf`;
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Letterhead template downloaded — print, sign, then re-upload below');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not download template');
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const uploadLetterhead = async (file) => {
    if (!file) return;
    setLetterheadInFlight(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(
        `${API}/portal/claims/${claimId}/wizard/step4/letterhead`,
        fd,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } },
      );
      if (res.data?.verdict === 'pass') {
        toast.success('Letterhead verified — all 4 checks passed');
      } else {
        toast.error('Letterhead failed AI checks — see remarks');
      }
      await loadClaim();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Letterhead upload failed');
    } finally {
      setLetterheadInFlight(false);
    }
  };

  const removeLetterhead = async () => {
    if (!window.confirm('Remove this letterhead and upload a new one?')) return;
    setLetterheadInFlight(true);
    try {
      await axios.delete(
        `${API}/portal/claims/${claimId}/wizard/step4/letterhead`,
        { withCredentials: true },
      );
      toast.success('Letterhead removed');
      await loadClaim();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not remove letterhead');
    } finally {
      setLetterheadInFlight(false);
    }
  };

  const completeStep4 = async () => {
    setCompletingStep4(true);
    try {
      await axios.post(
        `${API}/portal/claims/${claimId}/wizard/step4/complete`,
        null,
        { withCredentials: true },
      );
      toast.success('Step 4 complete — consolidated letterhead approved');
      await loadClaim();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not complete Step 4');
    } finally {
      setCompletingStep4(false);
    }
  };

  // ─── Step 5 — committee resolution upload (no AI) ─────
  const uploadResolution = async (file) => {
    if (!file) return;
    setResolutionInFlight(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(
        `${API}/portal/claims/${claimId}/wizard/step5/resolution`,
        fd,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } },
      );
      toast.success('Resolution uploaded');
      await loadClaim();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Resolution upload failed');
    } finally {
      setResolutionInFlight(false);
    }
  };

  const removeResolution = async () => {
    if (!window.confirm('Remove this resolution and upload a new one?')) return;
    setResolutionInFlight(true);
    try {
      await axios.delete(
        `${API}/portal/claims/${claimId}/wizard/step5/resolution`,
        { withCredentials: true },
      );
      toast.success('Resolution removed');
      await loadClaim();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not remove resolution');
    } finally {
      setResolutionInFlight(false);
    }
  };

  const completeStep5 = async () => {
    setCompletingStep5(true);
    try {
      await axios.post(
        `${API}/portal/claims/${claimId}/wizard/step5/complete`,
        null,
        { withCredentials: true },
      );
      toast.success('Step 5 complete — resolution recorded');
      await loadClaim();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not complete Step 5');
    } finally {
      setCompletingStep5(false);
    }
  };

  // ─── Step 6 — final submit ─────────────────────────────
  const submitClaim = async () => {
    if (!window.confirm('Submit this claim for MPL review? You cannot edit further once submitted.')) return;
    setSubmittingClaim(true);
    try {
      await axios.post(
        `${API}/portal/claims/${claimId}/wizard/submit`,
        null,
        { withCredentials: true },
      );
      toast.success('Claim submitted to MPL — you will be notified of the outcome');
      await loadClaim();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.message) {
        toast.error(detail.message);
      } else {
        toast.error(detail || 'Submission failed');
      }
    } finally {
      setSubmittingClaim(false);
    }
  };

  // ─── Iter45 — reset-to-step (cascade-clear downstream) ─
  const resetToStep = async (step, downstreamLabels) => {
    const msg = downstreamLabels.length
      ? `Editing this step will reset ${downstreamLabels.join(', ')}. Continue?`
      : 'Re-open this step for editing?';
    if (!window.confirm(msg)) return;
    setResettingStep(step);
    try {
      await axios.post(
        `${API}/portal/claims/${claimId}/wizard/reset-to/${step}`,
        null,
        { withCredentials: true },
      );
      toast.success(`Step ${step} re-opened for editing`);
      // Force the local rows mirror to re-seed from server next load.
      setRows([]);
      await loadClaim();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not reset step');
    } finally {
      setResettingStep(null);
    }
  };

  if (!claim) {
    return <div className="p-8 text-center text-muted-foreground">Loading wizard…</div>;
  }

  const step1 = claim.wizard_step_verdicts?.step1;
  const step1Passed = step1?.verdict === 'specific';
  const step1Failed = step1?.verdict === 'generic';
  const step2Passed = claim.wizard_step_verdicts?.step2?.verdict === 'pass';
  const step3Passed = claim.wizard_step_verdicts?.step3?.verdict === 'pass';
  const step4Passed = claim.wizard_step_verdicts?.step4?.verdict === 'pass';
  const step5Passed = claim.wizard_step_verdicts?.step5?.verdict === 'pass';
  // `claimSubmitted` controls the Submit button on Step 6 and the
  // green "submitted" banner. When the admin sends the claim back, status
  // stays `in_process` but `sent_back_pending` becomes true — in that
  // window the wizard should treat the claim as NOT submitted so the
  // secretary sees a fresh Submit button on Step 6 to resubmit after
  // their revisions.
  const claimSubmitted =
    ['submitted', 'in_process', 'approved', 'rejected', 'paid'].includes(claim.status)
    && !claim.sent_back_pending;
  // Iter45 — only allow Edit when claim hasn't been submitted yet,
  // OR the admin has bounced it back for revision (sent_back_pending).
  const canEdit = claim.status === 'draft' || !!claim.sent_back_pending;
  // Iter45 — sequential AI gate for Step 3: while any row is mid-check,
  // no other row can start a new upload.
  const anyStep3Busy = Object.values(quotationInFlight).some(Boolean);

  // Pre-bound props for the EditStepButton — passed at each call site.
  const editCommon = { canEdit, resettingStep, onReset: resetToStep };

  const rowsReady =
    rows.length > 0 &&
    rows.every((r) =>
      r.justification_verdict?.verdict === 'pass' &&
      Number(r.estimated_cost) > 0 &&
      Number(r.timeline_weeks) > 0
    );

  // Resolve the live item rows from `claim.proposed_items` so the
  // Step 3 UI always reflects the most recently-loaded server state
  // (quotation_verdict, quotation_attachment_id, etc.) without depending
  // on the local-edit `rows` mirror used by Step 2.
  const serverItems = claim?.proposed_items || [];
  const quotationsReady =
    serverItems.length > 0 &&
    serverItems.every((r) => (r.quotation_verdict?.verdict === 'pass'));

  return (
    <div className="max-w-5xl mx-auto py-8 space-y-6" data-testid="portal-claim-wizard">
      <div className="flex items-center gap-3">
        <Button asChild variant="ghost" size="sm">
          <Link to="/portal/claims"><ArrowLeft className="h-4 w-4 mr-1" /> Back to claims</Link>
        </Button>
        <h1 className="text-2xl font-bold tracking-tight">Grant Claim Wizard</h1>
        <Badge variant="secondary" className="ml-auto">Policy: {claim.policy?.title || claim.policy?.name || '—'}</Badge>
      </div>

      {/* Post-submission status trajectory — visible once the claim is
          submitted at least once. Hidden while the claim is still draft. */}
      <ClaimStatusTrajectory claim={claim} />

      <div className="rounded-md border bg-amber-50 border-amber-200 p-3 text-sm text-amber-900">
        <strong>All 6 steps live.</strong> Complete each step in order — the wizard auto-saves your progress.
      </div>

      {/* ─── Step 1 ───────────────────────────────────────── */}
      <Card data-testid="wizard-step1-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Step 1 — Nature &amp; Description of Proposed Infrastructure
            {step1Passed && <Badge className="bg-emerald-100 text-emerald-900">PASS</Badge>}
            {step1Failed && <Badge variant="destructive">REVISE &amp; RE-UPLOAD</Badge>}
            {step1Passed && (
              <EditStepButton {...editCommon}
                step={1}
                downstream={[
                  step2Passed && 'Step 2 (items)',
                  step3Passed && 'Step 3 (quotations)',
                  step4Passed && 'Step 4 (letterhead)',
                  step5Passed && 'Step 5 (resolution)',
                ].filter(Boolean)}
                label="Re-upload"
              />
            )}
          </CardTitle>
          <CardDescription>
            Upload the PDF that lists exactly what you want to build/procure. The AI will check the
            list is SPECIFIC (quantities, dimensions, brands) — not GENERIC (e.g. &quot;cricket equipment&quot;).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!step1Passed && (
            <div className="flex flex-wrap items-center gap-3">
              <Input
                type="file"
                accept="application/pdf,.pdf"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="max-w-md"
                data-testid="wizard-step1-file-input"
              />
              <Button onClick={runStep1} disabled={!file || uploading} data-testid="wizard-step1-submit-btn">
                {uploading ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Screening…</>
                ) : (
                  <><Upload className="h-4 w-4 mr-2" /> Upload &amp; screen</>
                )}
              </Button>
            </div>
          )}

          {step1 && (
            <div
              className={`rounded-md border p-3 text-sm ${
                step1Passed
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                  : 'bg-rose-50 border-rose-200 text-rose-900'
              }`}
              data-testid="wizard-step1-result"
            >
              <div className="flex items-start gap-2">
                {step1Passed
                  ? <CheckCircle2 className="h-5 w-5 mt-0.5" />
                  : <XCircle className="h-5 w-5 mt-0.5" />}
                <div className="flex-1">
                  <p className="font-medium">
                    {step1Passed ? 'PASS — list is specific' : 'FAIL — list is too generic'}
                  </p>
                  <p className="mt-1">{step1.remark}</p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ─── Step 2 ───────────────────────────────────────── */}
      {step1Passed && (
        <Card data-testid="wizard-step2-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              Step 2 — Justification, Cost &amp; Timeline
              {step2Passed && <Badge className="bg-emerald-100 text-emerald-900">COMPLETE</Badge>}
              {step2Passed && (
                <EditStepButton {...editCommon}
                  step={2}
                  downstream={[
                    step3Passed && 'Step 3 (quotations)',
                    step4Passed && 'Step 4 (letterhead)',
                    step5Passed && 'Step 5 (resolution)',
                  ].filter(Boolean)}
                />
              )}
              {savingRows && (
                <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                  <Loader2 className="h-3 w-3 animate-spin" /> Saving…
                </span>
              )}
            </CardTitle>
            <CardDescription>
              Justify each item with cricket-specific outcomes (age-group, season, # of players,
              measurable cricket metric). Generic claims like &quot;good for players&quot; are auto-rejected.
              Each row needs cost &gt; 0 and timeline &gt; 0 weeks.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Eligible-amount advisory meter. The division CAN submit a
                total above the policy's eligible amount — but anything
                above the cap won't be reimbursed. We surface this as a
                soft warning, not a blocking error. */}
            {(() => {
              const cap = Number(claim.policy?.eligible_amount) || 0;
              if (cap <= 0) return null;
              const total = rows.reduce(
                (s, r) => s + (Number(r.estimated_cost) || 0), 0
              );
              const over = total > cap;
              const pct = Math.min(100, Math.round((total / cap) * 100));
              return (
                <div
                  className={`rounded-md border p-3 text-xs ${
                    over ? 'border-amber-300 bg-amber-50 text-amber-900'
                    : pct >= 80 ? 'border-amber-300 bg-amber-50 text-amber-900'
                    : 'border-slate-200 bg-slate-50 text-slate-700'
                  }`}
                  data-testid="wizard-step2-cap-meter"
                >
                  <div className="flex justify-between items-center gap-2">
                    <span>
                      Policy eligible cap:{' '}
                      <strong>₹{cap.toLocaleString('en-IN')}</strong>
                    </span>
                    <span>
                      Running total:{' '}
                      <strong data-testid="wizard-step2-running-total">
                        ₹{total.toLocaleString('en-IN')}
                      </strong>
                      {' '}({pct}%)
                    </span>
                  </div>
                  <div className="h-1.5 mt-1.5 rounded bg-slate-200 overflow-hidden">
                    <div
                      className={`h-full ${
                        over ? 'bg-amber-500' : pct >= 80 ? 'bg-amber-500' : 'bg-emerald-500'
                      }`}
                      style={{ width: `${Math.min(100, pct)}%` }}
                    />
                  </div>
                  {over && (
                    <div className="mt-1.5">
                      <span className="font-medium">Note:</span> your ask is ₹
                      {(total - cap).toLocaleString('en-IN')} over the policy&apos;s
                      eligible cap. You can still submit this claim, but MPL
                      will approve <strong>only up to ₹{cap.toLocaleString('en-IN')}</strong>{' '}
                      based on the supporting documents.
                    </div>
                  )}
                </div>
              );
            })()}
            <div className="space-y-4" data-testid="wizard-step2-rows">
              {rows.length === 0 && (
                <div className="text-sm text-muted-foreground border rounded-md p-4 text-center">
                  No items extracted yet. Add a row manually or re-run Step 1.
                </div>
              )}
              {rows.map((row, idx) => {
                const v = row.justification_verdict;
                const aiBusy = !!justifyInFlight[row.id];
                const passed = v?.verdict === 'pass';
                const failed = v?.verdict === 'fail';
                return (
                  <div
                    key={row.id}
                    className={`border rounded-md p-4 space-y-3 ${
                      passed ? 'border-emerald-300 bg-emerald-50/40'
                      : failed ? 'border-rose-300 bg-rose-50/40'
                      : 'bg-card'
                    }`}
                    data-testid={`wizard-step2-row-${idx}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 grid grid-cols-1 md:grid-cols-12 gap-3">
                        <div className="md:col-span-5">
                          <label className="text-xs font-medium text-muted-foreground">
                            Item #{idx + 1}
                          </label>
                          <Input
                            value={row.item || ''}
                            onChange={(e) => updateRow(idx, { item: e.target.value })}
                            placeholder="e.g. 3-lane indoor net enclosure"
                            data-testid={`wizard-step2-item-${idx}`}
                            disabled={step2Passed}
                          />
                        </div>
                        <div className="md:col-span-7">
                          <label className="text-xs font-medium text-muted-foreground">Details</label>
                          <Input
                            value={row.details || ''}
                            onChange={(e) => updateRow(idx, { details: e.target.value })}
                            placeholder="quantity / dimensions / brand"
                            data-testid={`wizard-step2-details-${idx}`}
                            disabled={step2Passed}
                          />
                        </div>
                      </div>
                      {!step2Passed && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeRow(idx)}
                          data-testid={`wizard-step2-remove-${idx}`}
                          title="Remove row"
                        >
                          <Trash2 className="h-4 w-4 text-rose-600" />
                        </Button>
                      )}
                    </div>

                    <div>
                      <label className="text-xs font-medium text-muted-foreground">
                        Justification (cricket-specific outcomes)
                      </label>
                      <Textarea
                        value={row.justification || ''}
                        onChange={(e) => updateRow(idx, { justification: e.target.value })}
                        onBlur={() => justifyRow(row)}
                        placeholder="e.g. Enables monsoon training for U-16 academy trainees (28 players). Three lanes allow simultaneous pace, spin, and batting drills…"
                        rows={3}
                        data-testid={`wizard-step2-justification-${idx}`}
                        disabled={step2Passed}
                      />
                      <div className="flex items-center justify-between mt-1 gap-2 flex-wrap">
                        <div className="flex items-center gap-2 text-xs">
                          {aiBusy && (
                            <span className="inline-flex items-center gap-1 text-muted-foreground">
                              <Loader2 className="h-3 w-3 animate-spin" /> AI checking…
                            </span>
                          )}
                          {!aiBusy && passed && (
                            <Badge className="bg-emerald-100 text-emerald-900" data-testid={`wizard-step2-verdict-${idx}`}>
                              <CheckCircle2 className="h-3 w-3 mr-1" /> AI approved
                            </Badge>
                          )}
                          {!aiBusy && failed && (
                            <Badge variant="destructive" data-testid={`wizard-step2-verdict-${idx}`}>
                              <XCircle className="h-3 w-3 mr-1" /> Needs more detail
                            </Badge>
                          )}
                          {!aiBusy && !v && (row.justification || '').trim().length >= 10 && (
                            <span className="text-muted-foreground">Tab out to run AI check</span>
                          )}
                        </div>
                        {!step2Passed && (row.justification || '').trim().length >= 10 && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => justifyRow(row)}
                            disabled={aiBusy}
                            data-testid={`wizard-step2-justify-btn-${idx}`}
                          >
                            <Sparkles className="h-3 w-3 mr-1" />
                            {v ? 'Re-check' : 'Run AI check'}
                          </Button>
                        )}
                      </div>
                      {v?.remark && (
                        <p
                          className={`mt-2 text-xs leading-snug ${
                            passed ? 'text-emerald-800' : 'text-rose-800'
                          }`}
                          data-testid={`wizard-step2-remark-${idx}`}
                        >
                          {v.remark}
                        </p>
                      )}

                      {/* Iter46 — AI coaching panel: shown when verdict='fail'
                          and the AI has produced a suggested rewrite. Lets
                          the secretary one-click replace their textarea
                          with the AI's improved version. */}
                      {failed && (v?.suggested_rewrite || v?.actionable_tip) && (
                        <div
                          className="mt-3 rounded-md border border-sky-300 bg-sky-50 p-3 space-y-2"
                          data-testid={`wizard-step2-coaching-${idx}`}
                        >
                          {v.actionable_tip && (
                            <div className="flex items-start gap-2 text-xs text-sky-900">
                              <Sparkles className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                              <span><strong>Quick tip:</strong> {v.actionable_tip}</span>
                            </div>
                          )}
                          {v.missing_signals?.length > 0 && (
                            <div className="text-xs text-sky-900">
                              <strong>Add at least one of:</strong>{' '}
                              {v.missing_signals.join(' · ')}
                            </div>
                          )}
                          {v.suggested_rewrite && (
                            <div className="space-y-2">
                              <p className="text-xs font-semibold text-sky-900">
                                Suggested rewrite (you can use this as-is):
                              </p>
                              <p
                                className="text-xs leading-snug bg-white border border-sky-200 rounded px-2 py-2 text-slate-800"
                                data-testid={`wizard-step2-suggested-rewrite-${idx}`}
                              >
                                {v.suggested_rewrite}
                              </p>
                              <Button
                                size="sm"
                                variant="default"
                                onClick={() => {
                                  updateRow(idx, { justification: v.suggested_rewrite });
                                  toast.success('Suggested text inserted — click Re-check to verify');
                                }}
                                disabled={step2Passed}
                                data-testid={`wizard-step2-use-rewrite-btn-${idx}`}
                              >
                                <Sparkles className="h-3 w-3 mr-1" /> Use this rewrite
                              </Button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                      <div className="md:col-span-6">
                        <label className={`text-xs font-medium ${
                          Number(row.estimated_cost) > 0 ? 'text-muted-foreground' : 'text-rose-700'
                        }`}>
                          Estimated cost (INR)
                          {!(Number(row.estimated_cost) > 0) && !step2Passed && (
                            <span className="ml-1">— required (must be &gt; 0)</span>
                          )}
                        </label>
                        <Input
                          type="number"
                          min={0}
                          value={row.estimated_cost ?? 0}
                          onChange={(e) => updateRow(idx, { estimated_cost: e.target.value })}
                          data-testid={`wizard-step2-cost-${idx}`}
                          disabled={step2Passed}
                          className={
                            Number(row.estimated_cost) > 0 || step2Passed
                              ? ''
                              : 'border-rose-300 focus-visible:ring-rose-400 bg-rose-50/40'
                          }
                        />
                      </div>
                      <div className="md:col-span-6">
                        <label className={`text-xs font-medium ${
                          Number(row.timeline_weeks) > 0 ? 'text-muted-foreground' : 'text-rose-700'
                        }`}>
                          Timeline (weeks)
                          {!(Number(row.timeline_weeks) > 0) && !step2Passed && (
                            <span className="ml-1">— required (must be &gt; 0)</span>
                          )}
                        </label>
                        <Input
                          type="number"
                          min={0}
                          value={row.timeline_weeks ?? 0}
                          onChange={(e) => updateRow(idx, { timeline_weeks: e.target.value })}
                          data-testid={`wizard-step2-timeline-${idx}`}
                          disabled={step2Passed}
                          className={
                            Number(row.timeline_weeks) > 0 || step2Passed
                              ? ''
                              : 'border-rose-300 focus-visible:ring-rose-400 bg-rose-50/40'
                          }
                        />
                      </div>
                    </div>

                    {/* Iter47 — Per-row "what's missing" summary so users see
                        the exact reason their row isn't accepted by the
                        Continue gate. Hides when the row is fully ready. */}
                    {!step2Passed && (() => {
                      const blockers = [];
                      if (v?.verdict !== 'pass') blockers.push('AI approval');
                      if (!(Number(row.estimated_cost) > 0)) blockers.push('cost > 0');
                      if (!(Number(row.timeline_weeks) > 0)) blockers.push('timeline > 0');
                      if (!blockers.length) return null;
                      return (
                        <div
                          className="mt-1 text-xs text-rose-800 bg-rose-50 border border-rose-200 rounded px-2 py-1"
                          data-testid={`wizard-step2-row-blockers-${idx}`}
                        >
                          <strong>Missing on this row:</strong> {blockers.join(' · ')}
                        </div>
                      );
                    })()}
                  </div>
                );
              })}
            </div>

            {!step2Passed && (
              <div className="flex flex-wrap items-center gap-2 pt-2 border-t">
                <Button variant="outline" onClick={addRow} data-testid="wizard-step2-add-row-btn">
                  <Plus className="h-4 w-4 mr-1" /> Add row
                </Button>
                {canEdit && step1Passed && (
                  <Button
                    variant="outline"
                    onClick={() => resetToStep(1, ['Step 2 (items will be cleared)'])}
                    disabled={resettingStep != null}
                    data-testid="wizard-step2-previous-btn"
                  >
                    <ArrowLeft className="h-4 w-4 mr-1" /> Previous step
                  </Button>
                )}
                <div className="ml-auto flex items-center gap-3">
                  <span className="text-xs text-muted-foreground">
                    {rows.filter((r) => r.justification_verdict?.verdict === 'pass').length}/{rows.length} rows AI-approved
                  </span>
                  <Button
                    onClick={completeStep2}
                    disabled={!rowsReady || completing}
                    data-testid="wizard-step2-complete-btn"
                  >
                    {completing
                      ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…</>
                      : <>Continue to Step 3 <ArrowRight className="h-4 w-4 ml-1" /></>
                    }
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ─── Step 3 — Quotation upload per row ───────────── */}
      {step2Passed && (
        <Card data-testid="wizard-step3-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              Step 3 — Per-row Quotation Upload
              {step3Passed && <Badge className="bg-emerald-100 text-emerald-900">COMPLETE</Badge>}
              {step3Passed && (
                <EditStepButton {...editCommon}
                  step={3}
                  downstream={[
                    step4Passed && 'Step 4 (letterhead)',
                    step5Passed && 'Step 5 (resolution)',
                  ].filter(Boolean)}
                />
              )}
            </CardTitle>
            <CardDescription>
              Upload ONE vendor quotation PDF per item. The AI checks: (1) on
              vendor letterhead, (2) signed/stamped, (3) carries GSTIN
              <span className="text-muted-foreground/70">(if applicable)</span>,
              (4) total matches your estimated cost within <strong>±5%</strong>.
              Letterhead, signature and amount must all pass before you can
              continue to Step 4. <strong>GSTIN is optional</strong> — vendors
              without GST registration are accepted.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4" data-testid="wizard-step3-rows">
            {serverItems.length === 0 && (
              <div className="text-sm text-muted-foreground border rounded-md p-4 text-center">
                No items to attach quotations to.
              </div>
            )}
            {serverItems.map((row, idx) => {
              const qv = row.quotation_verdict;
              const hasQuote = !!row.quotation_attachment_id;
              const passed = qv?.verdict === 'pass';
              const failed = qv?.verdict === 'fail';
              const busy = !!quotationInFlight[row.id];
              const checks = qv?.checks || {};
              return (
                <div
                  key={row.id}
                  className={`border rounded-md p-4 space-y-3 ${
                    passed ? 'border-emerald-300 bg-emerald-50/40'
                    : failed ? 'border-rose-300 bg-rose-50/40'
                    : 'bg-card'
                  }`}
                  data-testid={`wizard-step3-row-${idx}`}
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-[240px]">
                      <p className="text-sm font-medium">
                        #{idx + 1} — {row.item || '(unnamed)'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {row.details || '—'} · Expected cost: ₹{Number(row.estimated_cost || 0).toLocaleString('en-IN')}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {busy && (
                        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                          <Loader2 className="h-3 w-3 animate-spin" /> Checking…
                        </span>
                      )}
                      {!busy && passed && (
                        <Badge className="bg-emerald-100 text-emerald-900" data-testid={`wizard-step3-verdict-${idx}`}>
                          <CheckCircle2 className="h-3 w-3 mr-1" /> Verified
                        </Badge>
                      )}
                      {!busy && failed && (
                        <Badge variant="destructive" data-testid={`wizard-step3-verdict-${idx}`}>
                          <XCircle className="h-3 w-3 mr-1" /> Failed AI checks
                        </Badge>
                      )}
                    </div>
                  </div>

                  {!hasQuote && !busy && !step3Passed && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <Input
                        type="file"
                        accept="application/pdf,.pdf"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (!f) return;
                          if (anyStep3Busy) {
                            toast.error('Please wait — verifying previous quotation first');
                            e.target.value = '';
                            return;
                          }
                          uploadQuotation(row, f);
                          e.target.value = '';
                        }}
                        disabled={busy || anyStep3Busy}
                        className="max-w-md"
                        data-testid={`wizard-step3-file-input-${idx}`}
                      />
                      <span className="text-xs text-muted-foreground">
                        {anyStep3Busy ? 'Waiting for previous quotation…' : 'PDF only'}
                      </span>
                    </div>
                  )}

                  {hasQuote && qv && (
                    <div className="space-y-2">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                        {['letterhead', 'signature', 'gstin', 'amount_match'].map((k) => {
                          // GSTIN is informational; render as neutral slate
                          // when missing ('na'), green when present.
                          const v = checks[k];
                          const isGstin = k === 'gstin';
                          const ok = v === 'pass';
                          const naGstin = isGstin && v !== 'pass';
                          const cls = ok
                            ? 'border-emerald-300 bg-emerald-50 text-emerald-900'
                            : naGstin
                            ? 'border-slate-300 bg-slate-50 text-slate-700'
                            : 'border-rose-300 bg-rose-50 text-rose-900';
                          const Icon = ok ? CheckCircle2 : naGstin ? null : XCircle;
                          return (
                            <div
                              key={k}
                              className={`rounded-md border px-2 py-1 flex items-center gap-1 ${cls}`}
                              data-testid={`wizard-step3-check-${k}-${idx}`}
                            >
                              {Icon ? <Icon className="h-3 w-3" /> : (
                                <span className="text-[10px] font-semibold">—</span>
                              )}
                              <span className="capitalize">
                                {isGstin && naGstin ? 'GSTIN: N/A'
                                  : k === 'amount_match' ? 'Amount ±5%' : k}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                      {(qv.vendor_name || qv.extracted_amount != null || qv.extracted_gstin) && (
                        <div className="text-xs text-muted-foreground flex flex-wrap gap-3">
                          {qv.vendor_name && <span>Vendor: <strong className="text-foreground">{qv.vendor_name}</strong></span>}
                          {qv.extracted_amount != null && (
                            <span>Quote total: <strong className="text-foreground">₹{Number(qv.extracted_amount).toLocaleString('en-IN')}</strong></span>
                          )}
                          {qv.extracted_gstin && <span>GSTIN: <strong className="text-foreground font-mono">{qv.extracted_gstin}</strong></span>}
                        </div>
                      )}
                      {qv.remark && (
                        <p
                          className={`text-xs leading-snug ${passed ? 'text-emerald-800' : 'text-rose-800'}`}
                          data-testid={`wizard-step3-remark-${idx}`}
                        >
                          {qv.remark}
                        </p>
                      )}

                      {/* Iter48 — AI coaching panel for failed quotations.
                          Shows actionable tip + vendor-side fixes +
                          secretary-side options. */}
                      {failed && (qv.actionable_tip || (qv.vendor_fixes?.length > 0) || (qv.secretary_options?.length > 0)) && (
                        <div
                          className="mt-2 rounded-md border border-sky-300 bg-sky-50 p-3 space-y-2"
                          data-testid={`wizard-step3-coaching-${idx}`}
                        >
                          {qv.actionable_tip && (
                            <div className="flex items-start gap-2 text-xs text-sky-900">
                              <Sparkles className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                              <span><strong>What to do next:</strong> {qv.actionable_tip}</span>
                            </div>
                          )}
                          {qv.vendor_fixes?.length > 0 && (
                            <div className="text-xs text-sky-900">
                              <strong>Ask the vendor to fix:</strong>
                              <ul className="list-disc pl-5 mt-1 space-y-0.5">
                                {qv.vendor_fixes.map((f, i) => <li key={i}>{f}</li>)}
                              </ul>
                            </div>
                          )}
                          {qv.secretary_options?.length > 0 && (
                            <div className="text-xs text-sky-900">
                              <strong>Or — your options:</strong>
                              <ul className="list-disc pl-5 mt-1 space-y-0.5">
                                {qv.secretary_options.map((f, i) => <li key={i}>{f}</li>)}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                      {!step3Passed && (
                        <div className="flex items-center gap-2 flex-wrap pt-1">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => removeQuotation(row)}
                            disabled={busy || anyStep3Busy}
                            data-testid={`wizard-step3-remove-btn-${idx}`}
                          >
                            <RefreshCw className="h-3 w-3 mr-1" /> Re-upload
                          </Button>
                          <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                            <FileText className="h-3 w-3" />
                            {anyStep3Busy
                              ? 'Waiting for previous quotation…'
                              : 'Uploaded — remove to attach a different PDF'
                            }
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            {!step3Passed && serverItems.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 pt-2 border-t">
                {canEdit && (
                  <Button
                    variant="outline"
                    onClick={() => resetToStep(2, ['Step 3 (quotations)'])}
                    disabled={resettingStep != null}
                    data-testid="wizard-step3-previous-btn"
                  >
                    <ArrowLeft className="h-4 w-4 mr-1" /> Previous step
                  </Button>
                )}
                <span className="text-xs text-muted-foreground ml-2">
                  {serverItems.filter((r) => r.quotation_verdict?.verdict === 'pass').length}/{serverItems.length} quotations verified
                </span>
                <Button
                  className="ml-auto"
                  onClick={completeStep3}
                  disabled={!quotationsReady || completingStep3}
                  data-testid="wizard-step3-complete-btn"
                >
                  {completingStep3
                    ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…</>
                    : <>Continue to Step 4 <ArrowRight className="h-4 w-4 ml-1" /></>
                  }
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ─── Step 4 — Consolidated Letterhead Request ─────── */}
      {step3Passed && (() => {
        const lv = claim.letterhead_verdict;
        const hasLetter = !!claim.letterhead_attachment_id;
        const passed = lv?.verdict === 'pass';
        const failed = lv?.verdict === 'fail';
        const checks = lv?.checks || {};
        const expectedAmount = lv?.expected_amount
          || (claim.proposed_items || []).reduce((s, r) => s + (Number(r.estimated_cost) || 0), 0);
        const tolerancePct = lv?.tolerance_pct ?? 2;

        return (
          <Card data-testid="wizard-step4-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                Step 4 — Consolidated Letterhead Request
                {step4Passed && <Badge className="bg-emerald-100 text-emerald-900">COMPLETE</Badge>}
                {step4Passed && (
                  <EditStepButton {...editCommon}
                    step={4}
                    downstream={[step5Passed && 'Step 5 (resolution)'].filter(Boolean)}
                  />
                )}
              </CardTitle>
              <CardDescription>
                Upload the formal consolidated request letter on your division&apos;s letterhead, signed by
                the President or Honorary Secretary. The AI checks: (1) division letterhead, (2) signatory
                name + designation, (3) every item in the letter matches your proposed items (no extras),
                (4) total within <strong>±{tolerancePct}%</strong> of ₹{Number(expectedAmount).toLocaleString('en-IN')}.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3" data-testid="wizard-step4-body">
              {!step4Passed && (
                <div
                  className="rounded-md border border-sky-200 bg-sky-50/60 p-3 flex flex-col sm:flex-row sm:items-center gap-3"
                  data-testid="wizard-step4-template-panel"
                >
                  <div className="flex-1 text-sm text-sky-900">
                    <p className="font-semibold flex items-center gap-1">
                      <FileText className="h-4 w-4" /> Don&apos;t have a letterhead PDF yet?
                    </p>
                    <p className="text-xs mt-1">
                      Download a pre-filled letterhead template (your division name, all proposed items,
                      grand total + empty signature blocks). Print it, sign it, scan it, then upload back below.
                    </p>
                  </div>
                  <Button
                    variant="default"
                    onClick={downloadLetterheadTemplate}
                    disabled={downloadingTemplate}
                    data-testid="wizard-step4-download-template-btn"
                  >
                    {downloadingTemplate
                      ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Preparing…</>
                      : <><Download className="h-4 w-4 mr-2" /> Download letterhead template</>
                    }
                  </Button>
                </div>
              )}

              {!hasLetter && !letterheadInFlight && !step4Passed && (
                <div className="flex items-center gap-2 flex-wrap">
                  <Input
                    type="file"
                    accept="application/pdf,.pdf"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) uploadLetterhead(f);
                      e.target.value = '';
                    }}
                    disabled={letterheadInFlight}
                    className="max-w-md"
                    data-testid="wizard-step4-file-input"
                  />
                  <span className="text-xs text-muted-foreground">PDF only</span>
                </div>
              )}

              {letterheadInFlight && (
                <div className="inline-flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> Running AI verification on the letterhead…
                </div>
              )}

              {hasLetter && lv && (
                <div className={`rounded-md border p-3 space-y-3 ${
                  passed ? 'border-emerald-300 bg-emerald-50/40'
                  : failed ? 'border-rose-300 bg-rose-50/40'
                  : 'bg-card'
                }`}>
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2">
                      {passed && (
                        <Badge className="bg-emerald-100 text-emerald-900" data-testid="wizard-step4-verdict">
                          <CheckCircle2 className="h-3 w-3 mr-1" /> Letterhead verified
                        </Badge>
                      )}
                      {failed && (
                        <Badge variant="destructive" data-testid="wizard-step4-verdict">
                          <XCircle className="h-3 w-3 mr-1" /> Failed AI checks
                        </Badge>
                      )}
                    </div>
                    {!step4Passed && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={removeLetterhead}
                        disabled={letterheadInFlight}
                        data-testid="wizard-step4-remove-btn"
                      >
                        <RefreshCw className="h-3 w-3 mr-1" /> Re-upload
                      </Button>
                    )}
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                    {[
                      ['division_letterhead', 'Letterhead'],
                      ['signatory', 'Signatory'],
                      ['items_match', 'Items match'],
                      ['amount_match', `Amount ±${tolerancePct}%`],
                    ].map(([k, label]) => (
                      <div
                        key={k}
                        className={`rounded-md border px-2 py-1 flex items-center gap-1 ${
                          checks[k] === 'pass'
                            ? 'border-emerald-300 bg-emerald-50 text-emerald-900'
                            : 'border-rose-300 bg-rose-50 text-rose-900'
                        }`}
                        data-testid={`wizard-step4-check-${k}`}
                      >
                        {checks[k] === 'pass'
                          ? <CheckCircle2 className="h-3 w-3" />
                          : <XCircle className="h-3 w-3" />}
                        <span>{label}</span>
                      </div>
                    ))}
                  </div>

                  {(lv.signatory_name || lv.extracted_amount != null) && (
                    <div className="text-xs text-muted-foreground flex flex-wrap gap-3">
                      {lv.signatory_name && (
                        <span>
                          Signatory: <strong className="text-foreground">{lv.signatory_name}</strong>
                          {lv.signatory_designation && <> · {lv.signatory_designation}</>}
                        </span>
                      )}
                      {lv.extracted_amount != null && (
                        <span>
                          Letter total: <strong className="text-foreground">
                            ₹{Number(lv.extracted_amount).toLocaleString('en-IN')}
                          </strong>
                        </span>
                      )}
                    </div>
                  )}

                  {(lv.missing_items?.length > 0 || lv.extra_items?.length > 0) && (
                    <div className="text-xs space-y-1">
                      {lv.missing_items?.length > 0 && (
                        <div className="text-rose-800" data-testid="wizard-step4-missing-items">
                          <strong>Missing from letter:</strong>{' '}
                          {lv.missing_items.join(', ')}
                        </div>
                      )}
                      {lv.extra_items?.length > 0 && (
                        <div className="text-rose-800" data-testid="wizard-step4-extra-items">
                          <strong>Extra items in letter (not in proposal):</strong>{' '}
                          {lv.extra_items.join(', ')}
                        </div>
                      )}
                    </div>
                  )}

                  {lv.remark && (
                    <p
                      className={`text-xs leading-snug ${passed ? 'text-emerald-800' : 'text-rose-800'}`}
                      data-testid="wizard-step4-remark"
                    >
                      {lv.remark}
                    </p>
                  )}
                </div>
              )}

              {!step4Passed && (
                <div className="flex items-center justify-between pt-2 border-t gap-2 flex-wrap">
                  {canEdit ? (
                    <Button
                      variant="outline"
                      onClick={() => resetToStep(3, ['Step 4 (letterhead)'])}
                      disabled={resettingStep != null}
                      data-testid="wizard-step4-previous-btn"
                    >
                      <ArrowLeft className="h-4 w-4 mr-1" /> Previous step
                    </Button>
                  ) : <span />}
                  {hasLetter && (
                    <Button
                      onClick={completeStep4}
                      disabled={!passed || completingStep4}
                      data-testid="wizard-step4-complete-btn"
                    >
                      {completingStep4
                        ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…</>
                        : <>Continue to Step 5 <ArrowRight className="h-4 w-4 ml-1" /></>
                      }
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })()}

      {/* ─── Step 5 — Committee Resolution upload (no AI) ──── */}
      {step4Passed && (
        <Card data-testid="wizard-step5-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              Step 5 — Committee Resolution
              {step5Passed && <Badge className="bg-emerald-100 text-emerald-900">COMPLETE</Badge>}
              {step5Passed && <EditStepButton {...editCommon} step={5} downstream={[]} />}
            </CardTitle>
            <CardDescription>
              Upload the Committee Resolution PDF authorising this grant request. No AI check — just attach the document.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3" data-testid="wizard-step5-body">
            {!claim.resolution_attachment_id && !resolutionInFlight && !step5Passed && (
              <div className="flex items-center gap-2 flex-wrap">
                <Input
                  type="file"
                  accept="application/pdf,.pdf"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) uploadResolution(f);
                    e.target.value = '';
                  }}
                  disabled={resolutionInFlight}
                  className="max-w-md"
                  data-testid="wizard-step5-file-input"
                />
                <span className="text-xs text-muted-foreground">PDF only</span>
              </div>
            )}

            {resolutionInFlight && (
              <div className="inline-flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Uploading resolution…
              </div>
            )}

            {claim.resolution_attachment_id && (
              <div className="rounded-md border border-emerald-300 bg-emerald-50/40 p-3 flex items-center justify-between gap-3 flex-wrap"
                   data-testid="wizard-step5-resolution-row">
                <div className="flex items-center gap-2 text-sm">
                  <FileText className="h-4 w-4 text-emerald-700" />
                  <div>
                    <p className="font-medium text-emerald-900">{claim.resolution_filename || 'resolution.pdf'}</p>
                    {claim.resolution_uploaded_at && (
                      <p className="text-xs text-muted-foreground">
                        Uploaded {new Date(claim.resolution_uploaded_at).toLocaleString()}
                      </p>
                    )}
                  </div>
                </div>
                {!step5Passed && !claimSubmitted && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={removeResolution}
                    disabled={resolutionInFlight}
                    data-testid="wizard-step5-remove-btn"
                  >
                    <RefreshCw className="h-3 w-3 mr-1" /> Re-upload
                  </Button>
                )}
              </div>
            )}

            {!step5Passed && (
              <div className="flex items-center justify-between pt-2 border-t gap-2 flex-wrap">
                {canEdit ? (
                  <Button
                    variant="outline"
                    onClick={() => resetToStep(4, ['Step 5 (resolution)'])}
                    disabled={resettingStep != null}
                    data-testid="wizard-step5-previous-btn"
                  >
                    <ArrowLeft className="h-4 w-4 mr-1" /> Previous step
                  </Button>
                ) : <span />}
                {claim.resolution_attachment_id && (
                  <Button
                    onClick={completeStep5}
                    disabled={completingStep5}
                    data-testid="wizard-step5-complete-btn"
                  >
                    {completingStep5
                      ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…</>
                      : <>Continue to Step 6 <ArrowRight className="h-4 w-4 ml-1" /></>
                    }
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ─── Step 6 — Final review + Submit ────────────────── */}
      {step5Passed && (() => {
        const lv = claim.letterhead_verdict || {};
        const items = claim.proposed_items || [];
        const grandTotal = items.reduce((s, r) => s + (Number(r.estimated_cost) || 0), 0);
        return (
          <Card data-testid="wizard-step6-card" className="border-emerald-300">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ClipboardList className="h-5 w-5" />
                Step 6 — Final Review &amp; Submit
                {claimSubmitted && <Badge className="bg-emerald-100 text-emerald-900">SUBMITTED</Badge>}
              </CardTitle>
              <CardDescription>
                Confirm everything below is correct. Submission is final — you cannot edit afterwards.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Step verdict checklist */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm" data-testid="wizard-step6-checklist">
                {[
                  ['Step 1 — Nature & Description', step1Passed],
                  ['Step 2 — Items & Justification', step2Passed],
                  ['Step 3 — Quotations verified', step3Passed],
                  ['Step 4 — Letterhead signed & verified', step4Passed],
                  ['Step 5 — Committee Resolution attached', step5Passed],
                ].map(([label, ok]) => (
                  <div key={label} className={`rounded-md border px-3 py-2 flex items-center gap-2 ${
                    ok ? 'border-emerald-300 bg-emerald-50 text-emerald-900'
                       : 'border-rose-300 bg-rose-50 text-rose-900'
                  }`}>
                    {ok ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                    <span>{label}</span>
                  </div>
                ))}
              </div>

              {/* Summary panel */}
              <div className="rounded-md border bg-card p-3 space-y-2 text-sm" data-testid="wizard-step6-summary">
                <div className="flex justify-between gap-3 border-b pb-2">
                  <span className="text-muted-foreground">Policy</span>
                  <span className="font-medium">{claim.policy?.title || claim.policy?.name || '—'}</span>
                </div>
                <div className="flex justify-between gap-3 border-b pb-2">
                  <span className="text-muted-foreground">Items proposed</span>
                  <span className="font-medium">{items.length}</span>
                </div>
                <div className="flex justify-between gap-3 border-b pb-2">
                  <span className="text-muted-foreground">Grand total</span>
                  <span className="font-medium">₹{Number(grandTotal).toLocaleString('en-IN')}</span>
                </div>
                {lv.signatory_name && (
                  <div className="flex justify-between gap-3 border-b pb-2">
                    <span className="text-muted-foreground">Letterhead signed by</span>
                    <span className="font-medium">{lv.signatory_name} {lv.signatory_designation && `· ${lv.signatory_designation}`}</span>
                  </div>
                )}
                <div className="flex justify-between gap-3">
                  <span className="text-muted-foreground">Resolution</span>
                  <span className="font-medium font-mono text-xs truncate max-w-[60%]">{claim.resolution_filename || '—'}</span>
                </div>
              </div>

              {/* Items breakdown */}
              <div className="rounded-md border" data-testid="wizard-step6-items">
                <div className="px-3 py-2 border-b text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Proposed items
                </div>
                <div className="divide-y">
                  {items.map((r, idx) => (
                    <div key={r.id} className="px-3 py-2 flex items-start justify-between gap-3 text-sm">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">#{idx + 1} — {r.item}</p>
                        {r.details && <p className="text-xs text-muted-foreground truncate">{r.details}</p>}
                      </div>
                      <div className="text-right shrink-0">
                        <p className="font-medium">₹{Number(r.estimated_cost || 0).toLocaleString('en-IN')}</p>
                        <p className="text-xs text-muted-foreground">{r.timeline_weeks || 0} wks</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {!claimSubmitted && (
                <div className="flex items-center justify-between pt-3 border-t gap-2 flex-wrap">
                  {canEdit ? (
                    <Button
                      variant="outline"
                      onClick={() => resetToStep(5, [])}
                      disabled={resettingStep != null}
                      data-testid="wizard-step6-previous-btn"
                    >
                      <ArrowLeft className="h-4 w-4 mr-1" /> Previous step
                    </Button>
                  ) : <span />}
                  <Button
                    size="lg"
                    onClick={submitClaim}
                    disabled={submittingClaim}
                    className="bg-emerald-700 hover:bg-emerald-800 text-white"
                    data-testid="wizard-step6-submit-btn"
                  >
                    {submittingClaim
                      ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Submitting…</>
                      : <><Send className="h-4 w-4 mr-2" /> Submit for MPL Review</>
                    }
                  </Button>
                </div>
              )}

              {claimSubmitted && (
                <div className="rounded-md border border-emerald-300 bg-emerald-50 p-4 text-sm text-emerald-900" data-testid="wizard-step6-submitted-banner">
                  <p className="font-semibold flex items-center gap-1">
                    <CheckCircle2 className="h-4 w-4" /> Claim submitted to MPL
                  </p>
                  <p className="text-xs mt-1">
                    {claim.submitted_at && (
                      <>Submitted {new Date(claim.submitted_at).toLocaleString()}. </>
                    )}
                    The Hon. Secretary has been notified by email. You&apos;ll be updated when the claim is reviewed.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })()}

      {/* Activity log — every status change captured by the backend's
          status_history. Visible on every wizard view so the secretary
          can always see who did what and when. */}
      <ActivityLog history={claim.status_history} />
    </div>
  );
}
